import { parseISO } from "date-fns";
import { addCalendarDays } from "@common/helpers/date";
import type { FiltersTypes } from "@common/types/filters/general";
import {
    WORK_DELIVERY_DATE_PRESETS,
    WorkDeliveryDatePreset,
} from "@common/types/workDeliveryDatePreset";
import {
    getMonthRange,
    toDateOnly,
} from "@design-system/DatePicker/DatePicker.lib";
import type { DateOnlyRange } from "@design-system/DatePicker/DatePicker.types";
import type { RelativeDatePreset } from "@components/pageHeader/filters/AggregatedFilters/DateFilter/AggregatedDateFilter";

export const WORK_DELIVERY_DATE_PRESET_LABELS: Record<
    WorkDeliveryDatePreset,
    string
> = {
    before_today: "Before today",
    next_14_days: "Next 14 days",
    this_month: "This month",
    no_due_date: "No due date",
};

interface ResolveWorkDatePresetParams {
    preset: WorkDeliveryDatePreset;
    /** The viewer's browser date (`YYYY-MM-DD`). */
    today: string;
}

/** The Work end calendar days a preset covers on `today`; a missing bound is open. */
export function resolveWorkDatePreset({
    preset,
    today,
}: ResolveWorkDatePresetParams): DateOnlyRange {
    switch (preset) {
        case "before_today":
            return { to: addCalendarDays(today, -1) };
        case "next_14_days":
            return { from: today, to: addCalendarDays(today, 13) };
        case "this_month": {
            const { from, to } = getMonthRange(parseISO(today));
            return { from: toDateOnly(from), to: toDateOnly(to) };
        }
        case "no_due_date":
            return {};
    }
}

/** The view filters that pick `preset`; a preset replaces any explicit Work end dates. */
export const buildWorkDeliveryDatePresetPatch = (
    preset: WorkDeliveryDatePreset,
): Partial<FiltersTypes> => ({
    workDeliveryDatePreset: preset,
    workDeliveryDateStart: undefined,
    workDeliveryDateEnd: undefined,
});

/** The presets the Work end filter offers, resolved for display on `today`. */
export function buildWorkDeliveryDatePresets(
    today: string,
): Array<RelativeDatePreset<WorkDeliveryDatePreset>> {
    return WORK_DELIVERY_DATE_PRESETS.map((preset) => ({
        id: preset,
        label: WORK_DELIVERY_DATE_PRESET_LABELS[preset],
        value: resolveWorkDatePreset({ preset, today }),
    }));
}
